import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faSun } from '@fortawesome/pro-solid-svg-icons/faSun';
import { faMoon } from '@fortawesome/pro-solid-svg-icons/faMoon';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { Button, ButtonType } from '../../../components';
import { DAYS_OF_WEEK, DayOfWeek, TIMES_OF_DAY, TimeOfDay } from '../constants';

interface CellToggleProps {
  icon: IconDefinition;
  iconActiveClass: string;
  ariaLabel: string;
  active: boolean;
  onClick: () => void;
}

const CellToggle = ({
  icon,
  iconActiveClass,
  ariaLabel,
  active,
  onClick,
}: CellToggleProps): JSX.Element => (
  <Button
    aria-label={ariaLabel}
    aria-pressed={active}
    onClick={onClick}
    className='flex items-center justify-center bg-transparent border-0 p-1 rounded hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-sapphire'
  >
    <FontAwesomeIcon
      icon={icon}
      size='lg'
      className={active ? iconActiveClass : 'text-neutral-300'}
    />
  </Button>
);

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type Selection = Record<DayOfWeek['filterKey'], Set<number>>;

const buildSelection = (params: URLSearchParams): Selection => {
  const next = {} as Selection;
  DAYS_OF_WEEK.forEach((day) => {
    const raw = params.get(day.filterKey);
    next[day.filterKey] = new Set(
      raw
        ? raw
            .split(',')
            .map((s) => Number(s))
            .filter((n) => !Number.isNaN(n))
        : [],
    );
  });
  return next;
};

// Detailed availability editor. Stages selections locally and commits via URL
// params only when the user clicks Apply, avoiding one search request per
// toggle.
export const EditAvailabilityModal = ({ isOpen, onClose }: Props): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const [searchParams, setSearchParams] = useSearchParams();
  const [staged, setStaged] = useState<Selection>(() => buildSelection(searchParams));
  const [exact, setExact] = useState<boolean>(
    () => searchParams.get('exactRecurringAvailability') === 'true',
  );

  // Re-sync whenever the modal opens so stale staging doesn't linger.
  useEffect(() => {
    if (isOpen) {
      setStaged(buildSelection(searchParams));
      setExact(searchParams.get('exactRecurringAvailability') === 'true');
    }

  }, [isOpen]);

  const isSelected = (day: DayOfWeek, time: TimeOfDay): boolean =>
    staged[day.filterKey].has(time.id);

  const toggle = (day: DayOfWeek, time: TimeOfDay): void => {
    setStaged((prev) => {
      const next = { ...prev };
      const set = new Set(next[day.filterKey]);
      if (set.has(time.id)) set.delete(time.id);
      else set.add(time.id);
      next[day.filterKey] = set;
      return next;
    });
  };

  const apply = (): void => {
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        DAYS_OF_WEEK.forEach((day) => {
          const ids = staged[day.filterKey];
          if (ids.size === 0) next.delete(day.filterKey);
          else next.set(day.filterKey, [...ids].sort((a, b) => a - b).join(','));
        });
        if (exact) next.set('exactRecurringAvailability', 'true');
        else next.delete('exactRecurringAvailability');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sittersEditAvailability.title')}
      className='w-full max-w-lg'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button buttonType={ButtonType.primary} onClick={apply}>
            {t('common.apply')}
          </Button>
        </>
      }
    >
      <h4 className='font-semibold mb-1'>{t('pages.sittersEditAvailability.generallyAvailable')}</h4>
      <label className='flex items-center gap-2 text-sm mb-3'>
        <input
          id='exact-matches-toggle'
          type='checkbox'
          checked={exact}
          onChange={(e): void => setExact(e.target.checked)}
        />
        <span>{t('pages.sittersEditAvailability.showOnlyExactMatches')}</span>
      </label>

      <div className='grid grid-cols-[2.25rem_repeat(7,minmax(0,1fr))] md:grid-cols-[2.25rem_repeat(7,minmax(0,1fr))_minmax(0,11rem)] gap-x-2 gap-y-3 items-center text-center mb-4'>
        <div />
        {DAYS_OF_WEEK.map((d) => (
          <div key={`head-${d.code}`} className='font-semibold text-sm'>
            {d.code.charAt(0)}
          </div>
        ))}
        <div className='hidden md:block' />

        <div className='text-left font-semibold text-sm'>{t('pages.sittersEditAvailability.am')}</div>
        {DAYS_OF_WEEK.map((d) => (
          <CellToggle
            key={`MORNING-${d.code}`}
            icon={faSun}
            iconActiveClass='text-yellow-500'
            ariaLabel={t('pages.sittersEditAvailability.morningAriaLabel', { day: d.name })}
            active={isSelected(d, TIMES_OF_DAY.MORNING)}
            onClick={(): void => toggle(d, TIMES_OF_DAY.MORNING)}
          />
        ))}
        <div className='hidden md:block text-left text-sm text-neutral-700'>
          {t('pages.sittersEditAvailability.morningDescription')}
        </div>

        <div className='text-left font-semibold text-sm'>{t('pages.sittersEditAvailability.pm')}</div>
        {DAYS_OF_WEEK.map((d) => (
          <CellToggle
            key={`AFTERNOON-${d.code}`}
            icon={faSun}
            iconActiveClass='text-yellow-500'
            ariaLabel={t('pages.sittersEditAvailability.afternoonAriaLabel', { day: d.name })}
            active={isSelected(d, TIMES_OF_DAY.AFTERNOON)}
            onClick={(): void => toggle(d, TIMES_OF_DAY.AFTERNOON)}
          />
        ))}
        <div className='hidden md:block text-left text-sm text-neutral-700'>
          {t('pages.sittersEditAvailability.afternoonDescription')}
        </div>

        <div className='text-left font-semibold text-sm'>{t('pages.sittersEditAvailability.night')}</div>
        {DAYS_OF_WEEK.map((d) => (
          <CellToggle
            key={`NIGHT-${d.code}`}
            icon={faMoon}
            iconActiveClass='text-sapphire'
            ariaLabel={t('pages.sittersEditAvailability.nightAriaLabel', { day: d.name })}
            active={isSelected(d, TIMES_OF_DAY.NIGHT)}
            onClick={(): void => toggle(d, TIMES_OF_DAY.NIGHT)}
          />
        ))}
        <div className='hidden md:block text-left text-sm text-neutral-700'>
          {t('pages.sittersEditAvailability.nightDescription')}
        </div>
      </div>

      <p className='text-sm text-neutral-600 mb-4'>
        {t('pages.sittersEditAvailability.postJobPrefix')}{' '}
        <a id='post-job' href='/app/job-post' className='underline font-medium'>
          {t('pages.sittersEditAvailability.postJobLink')}
        </a>
      </p>
    </Modal>
  );
};
